import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { getProviderOffers, updateOfferWithMedia } from "../services/offerService";
import { toast } from "sonner";
import { StatusFilter } from "@/components/offers/StatusFilter";
import { OffersPlaceholder } from "@/components/offers/OffersPlaceholder";
import { MessageCircle, ChevronRight, MoreVertical, Edit, Trash2 } from "lucide-react";
import { Offer } from "@/types/offer";
import { authenticatedGet, authenticatedDelete } from "../utils/apiUtils";
import { getAccessToken } from "../services/authService";
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import OfferForm from "../components/OfferForm";

const MyOffers = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("newest");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    loadOffers();
  }, [isAuthenticated, navigate]);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const userId = user?.userId || 
                    localStorage.getItem('userId') || 
                    localStorage.getItem('user_id');
      
      if (!userId) {
        console.error('No user ID found for fetching offers');
        setLoading(false);
        return;
      }
      
      console.log('Fetching offers with user ID:', userId);
      
      const response = await api.get(`/offers/provider/${userId}`);
      
      if (response.data) {
        setOffers(response.data);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error("Failed to load your offers");
    } finally {
      setLoading(false);
    }
  };

  const handleEditOffer = (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOffer(offer);
    setShowEditForm(true);
  };

  const handleDeleteClick = (offerId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOfferId(offerId);
    setShowDeleteDialog(true);
  };

  const handleDeleteOffer = async () => {
    if (!selectedOfferId) return;
    
    try {
      const response = await authenticatedDelete(
        `${import.meta.env.VITE_API_URL}/offers/${selectedOfferId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete offer');
      }
      
      setOffers(offers.filter(offer => offer.id !== selectedOfferId));
      toast.success('Offer deleted successfully');
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    } finally {
      setShowDeleteDialog(false);
      setSelectedOfferId(null);
    }
  };

  const getProgressValue = (status: string): number => {
    switch (status) {
      case 'PENDING': return 25;
      case 'IN_PROGRESS': return 75;
      case 'COMPLETED': return 100;
      case 'REJECTED': return 0;
      default: return 0;
    }
  };

  const statusColors = {
    PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
    IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-300",
    COMPLETED: "bg-green-100 text-green-800 border-green-300",
    REJECTED: "bg-red-100 text-red-800 border-red-300"
  };

  const filteredOffers = statusFilter === "all" 
    ? offers 
    : offers.filter(offer => offer.status === statusFilter);

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case "oldest":
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case "price-high":
        return b.price - a.price;
      case "price-low":
        return a.price - b.price;
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Offers</h1>
            <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          </div>
          
          <OffersPlaceholder 
            isLoading={loading}
            isEmpty={sortedOffers.length === 0}
            statusFilter={statusFilter}
          />

          {!loading && sortedOffers.length > 0 && (
            <div className="grid gap-4">
              {sortedOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex justify-between items-start">
                    <div 
                      className="flex-grow"
                      onClick={() => navigate(`/my-offers/${offer.id}`, { state: { offer } })}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 border ${statusColors[offer.status as keyof typeof statusColors]}`}>
                          {offer.status}
                        </span>
                        <span className="text-sm text-gray-500">
                          Offered on {new Date(offer.createdAt).toLocaleDateString()}
                        </span>
                        
                        {offer.updatedAt && offer.updatedAt !== offer.createdAt && (
                          <span className="text-xs text-gray-500 italic">
                            (Edited: {new Date(offer.updatedAt).toLocaleDateString()})
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {offer.willTitle}
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-sm text-gray-600">
                          Price: {offer.currency || '₪'}{offer.price}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MessageCircle className="h-4 w-4" />
                          <span className="text-sm">
                            Contact: {offer.providerContactInfo}
                          </span>
                        </div>
                      </div>

                      <div className="text-sm text-gray-600 line-clamp-2 mb-4">
                        {offer.description}
                      </div>

                      <div className="space-y-3">
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-700 ease-in-out bg-gradient-to-r from-yellow-400 via-blue-500 to-green-500"
                            style={{ width: `${getProgressValue(offer.status)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            📝 Offer Sent
                          </span>
                          <span className="flex items-center gap-1">
                            👀 In Review
                          </span>
                          <span className="flex items-center gap-1">
                            🔨 In Progress
                          </span>
                          <span className="flex items-center gap-1">
                            ✨ Completed
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 rounded-full hover:bg-gray-100">
                            <MoreVertical className="h-5 w-5 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={(e) => handleEditOffer(offer, e)}
                            disabled={offer.status === 'COMPLETED' || offer.status === 'REJECTED'}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Offer
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => handleDeleteClick(offer.id, e)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Offer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your offer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOffer}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Edit Offer Form Dialog */}
      {showEditForm && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <OfferForm
              willId={selectedOffer.willId}
              offerId={selectedOffer.id}
              isEditMode={true}
              initialData={{
                description: selectedOffer.description,
                price: selectedOffer.price?.toString() || '',
                currency: selectedOffer.currency || 'ILS',
                estimatedCompletionDate: selectedOffer.estimatedCompletionDate || '',
                providerContactInfo: selectedOffer.providerContactInfo || ''
              }}
              existingMedia={selectedOffer.media || []}
              onSuccess={(updatedOffer) => {
                setOffers(offers.map(o => 
                  o.id === updatedOffer.id ? updatedOffer : o
                ));
                setShowEditForm(false);
                toast.success('Offer updated successfully');
              }}
              onCancel={() => setShowEditForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOffers;
